import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { spring, ease } from '@/lib/motion';
import WizardProgress from './WizardProgress';
import WelcomeStep from './steps/WelcomeStep';
import ConnectGoogleStep from './steps/ConnectGoogleStep';
import SyncProgressStep from './steps/SyncProgressStep';
import SuccessStep from './steps/SuccessStep';
import SelectFeedStep from './steps/SelectFeedStep';
import DownloadProductsStep from './steps/DownloadProductsStep';
import ReadyStep from './steps/ReadyStep';
import { getWizardStepIds, advanceFromStep, type WizardStepId } from '@/lib/wizardSteps';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Spinner } from '@/components/ui/spinner';

interface GoogleAccountWizardProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (googleAccountId: string | null) => void;
  /** Onboarding only: invoked from ReadyStep to kick off optimization of all products. */
  onOptimizeAll?: (productCount: number) => void;
  mode?: 'onboarding' | 'connect';
}

export interface WizardData {
  googleAccountId: string | null;
  merchantCenterCount: number;
  feedCount: number;
  /** The feed the user explicitly chose in SelectFeedStep — threaded to the download step. */
  feedId: string | null;
  productCount: number;
}

const stepVariants = {
  enter: (dir: number) => ({
    x: dir > 0 ? 60 : -60,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: spring.snappy,
  },
  exit: (dir: number) => ({
    x: dir > 0 ? -60 : 60,
    opacity: 0,
    transition: ease.exit,
  }),
};

const GoogleAccountWizard: React.FC<GoogleAccountWizardProps> = ({
  open,
  onClose,
  onSuccess,
  onOptimizeAll,
  mode = 'onboarding',
}) => {
  const { t } = useTranslation('wizard');
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [condensed, setCondensed] = useState(false);
  const [checking, setChecking] = useState(mode === 'onboarding');
  const [wizardData, setWizardData] = useState<WizardData>({
    googleAccountId: null,
    merchantCenterCount: 0,
    feedCount: 0,
    feedId: null,
    productCount: 0,
  });

  // Memoised so the array reference is stable across renders. This is load-bearing:
  // nextStep/goToStepId/advanceFromSync depend on it, and those callbacks are passed
  // (as onNext) to the @protected SyncProgressStep. If stepIds were a fresh array each
  // render, those callbacks would change identity every render, SyncProgressStep's poll
  // useEffect (deps include onNext/onComplete) would re-run on every render, and its
  // uncleared setTimeout(onNext, 1500) would be re-scheduled repeatedly — the leftover
  // timeout then skips the feed-selection step straight to download.
  const stepIds = useMemo(() => getWizardStepIds(mode, condensed), [mode, condensed]);
  const totalSteps = stepIds.length;

  const STEP_LABEL_KEY: Record<WizardStepId, string> = {
    welcome: 'steps.welcome',
    connect: 'steps.connect',
    sync: condensed ? 'steps.syncing' : 'steps.sync',
    success: condensed ? 'steps.done' : 'steps.complete',
    selectFeed: 'steps.selectFeed',
    download: 'steps.download',
    ready: 'steps.ready',
  };
  const wizardSteps = stepIds.map((id) => t(STEP_LABEL_KEY[id]));

  // Check if user already has a google account connected (from unified auth flow)
  useEffect(() => {
    if (!open || !user) return;

    // In 'connect' mode (Settings), always show the full wizard
    if (mode === 'connect') {
      setCondensed(false);
      setChecking(false);
      return;
    }

    const checkExistingAccount = async () => {
      try {
        const { data } = await supabase
          .from('google_accounts')
          .select('id')
          .eq('user_id', user.id)
          .limit(1)
          .maybeSingle();

        if (data?.id) {
          // Account exists — condensed mode (Sync → Done)
          setWizardData(prev => ({ ...prev, googleAccountId: data.id }));
          setCondensed(true);
          setDirection(1);
          setCurrentStep(1);
        }
      } finally {
        setChecking(false);
      }
    };

    checkExistingAccount();
  }, [open, user, mode]);

  // All step callbacks are memoised. Stable identities keep SyncProgressStep's poll
  // useEffect from re-running every render (see the stepIds useMemo note above).
  const updateWizardData = useCallback((data: Partial<WizardData>) => {
    setWizardData((prev) => ({ ...prev, ...data }));
  }, []);

  const nextStep = useCallback(() => {
    setDirection(1);
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
  }, [totalSteps]);

  const prevStep = useCallback(() => {
    setDirection(-1);
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  }, []);

  const goToStepId = useCallback((id: WizardStepId) => {
    const idx = stepIds.indexOf(id);
    if (idx >= 0) {
      setDirection(-1);
      setCurrentStep(idx + 1);
    }
  }, [stepIds]);

  // Guarded advance for the sync step: only moves forward if we're STILL on 'sync'.
  // Neutralises any leftover setTimeout(onNext) from the @protected SyncProgressStep
  // that fires after the wizard already advanced to the feed-selection step.
  const advanceFromSync = useCallback(() => {
    setDirection(1);
    setCurrentStep((prev) => advanceFromStep(stepIds, prev, 'sync'));
  }, [stepIds]);

  const handleClose = () => {
    setCurrentStep(1);
    setDirection(1);
    setCondensed(false);
    setChecking(mode === 'onboarding');
    setWizardData({
      googleAccountId: null,
      merchantCenterCount: 0,
      feedCount: 0,
      feedId: null,
      productCount: 0,
    });
    onClose();
  };

  const handleComplete = () => {
    const accountId = wizardData.googleAccountId;
    handleClose();
    onSuccess?.(accountId);
  };

  const renderStep = () => {
    const stepId = stepIds[currentStep - 1];
    switch (stepId) {
      case 'welcome':
        return <WelcomeStep onNext={nextStep} />;
      case 'connect':
        return (
          <ConnectGoogleStep
            onNext={nextStep}
            onBack={prevStep}
            onGoogleAccountCreated={(accountId) => {
              updateWizardData({ googleAccountId: accountId });
              nextStep();
            }}
          />
        );
      case 'sync':
        // onNext + onComplete MUST be stable references here. SyncProgressStep (@protected)
        // re-runs its poll effect whenever these change and schedules an uncleared
        // setTimeout(onNext, 1500); unstable props re-arm it repeatedly and the leftover
        // timeout skips the feed-selection step. advanceFromSync also guards off-step fires.
        return (
          <SyncProgressStep
            googleAccountId={wizardData.googleAccountId}
            onNext={advanceFromSync}
            onComplete={updateWizardData}
          />
        );
      case 'selectFeed':
        return (
          <SelectFeedStep
            googleAccountId={wizardData.googleAccountId}
            onNext={(feedId) => {
              updateWizardData({ feedId });
              nextStep();
            }}
            onBack={mode === 'onboarding' && !condensed ? prevStep : undefined}
            onFinish={handleComplete}
          />
        );
      case 'download':
        return (
          <DownloadProductsStep
            feedId={wizardData.feedId}
            onDone={(productCount) => {
              updateWizardData({ productCount });
              nextStep();
            }}
          />
        );
      case 'ready':
        return (
          <ReadyStep
            productCount={wizardData.productCount}
            onOptimizeAll={() => {
              const count = wizardData.productCount;
              handleClose();
              onOptimizeAll?.(count);
            }}
            onFinish={handleComplete}
            onBack={() => goToStepId('selectFeed')}
          />
        );
      case 'success':
        return (
          <SuccessStep
            merchantCenterCount={wizardData.merchantCenterCount}
            feedCount={wizardData.feedCount}
            onFinish={handleComplete}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
        {/* S2 (WCAG 4.1.2): der Onboarding-Assistent ist ein Dialog ohne
            Radix-Titel — die sichtbare Ueberschrift traegt WizardProgress als
            nacktes Markup und fehlt im checking-Zustand komplett. Ein sr-only
            DialogTitle gibt dem Dialog in jedem Zustand einen Namen, ohne die
            Optik anzufassen. */}
        <DialogTitle className="sr-only">
          {condensed ? t('titles.settingUp') : t('titles.connectMerchant')}
        </DialogTitle>
        {checking ? (
          <div className="flex items-center justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : (
          <div className="space-y-4">
            <WizardProgress
              steps={wizardSteps}
              currentStep={currentStep}
              title={condensed ? t('titles.settingUp') : t('titles.connectMerchant')}
            />
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={currentStep}
                custom={direction}
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
              >
                {renderStep()}
              </motion.div>
            </AnimatePresence>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GoogleAccountWizard;
